// Core
import { Fragment, useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Components
import { Button, Chip, Crumbs, FieldSelect, FieldText, Segmented } from "components/AtomsV2";
import { Icon } from "components/ShellV2";
import HelpFloatingButtonV2 from "components/HelpFloatingButtonV2";

// Redux
import { useDispatch, useSelector } from "react-redux";
import { openNotificationPopup, openSuccessAlert } from "state/application/applicationSlice";
import { selectCurrentCommunity } from "state/communities/communitiesSlice";
import { useAddNewResourceMutation } from "state/resources/resourcesApiSlice";
import { selectAllMembers, useGetMembersByCommunityIdQuery } from "state/members/membersApiSlice";

// Utils
import { avColor, initials } from "utils/avatar";
import { resourceVisual } from "utils/resourceTypes";
import { DETAIL_FIELDS } from "pages/ManageResources/Single/Details";

const RESOURCE_TYPES = ["SCPU", "STORAGE", "EV", "EWH", "HP", "IC", "IU"];

const emptyDetails = (type) =>
  (DETAIL_FIELDS[type] || []).reduce((acc, f) => {
    acc[f.key] = f.input ? "" : 0;
    return acc;
  }, {});

/* Stepper header (design's WizSteps) */
function WizSteps({ step, steps }) {
  return (
    <div className="rec-wiz-steps">
      {steps.map((s, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <Fragment key={i}>
          {i > 0 && <div className={`rec-wiz-line ${step >= i ? "on" : ""}`} />}
          <div className={`rec-wiz-step ${i === step ? "cur" : ""} ${i < step ? "done" : ""}`}>
            <div className="rec-wiz-dot">
              {i < step ? <Icon name="check" style={{ fontSize: 16 }} /> : i + 1}
            </div>
            <div className="rec-wiz-lbl">
              <b>{s.label}</b>
              <small>{s.sub}</small>
            </div>
          </div>
        </Fragment>
      ))}
    </div>
  );
}

WizSteps.propTypes = {
  step: PropTypes.number.isRequired,
  steps: PropTypes.arrayOf(PropTypes.shape({ label: PropTypes.node, sub: PropTypes.node }))
    .isRequired,
};

/* 2-step resource creation wizard (design's ResourceCreateWizard):
   step 1 — designation, type picker, status and optional owners;
   step 2 — type-specific technical details. */
function NewResource() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation(["resources", "community"]);

  const community = useSelector(selectCurrentCommunity);

  const [step, setStep] = useState(0);
  const [name, setName] = useState("");
  const [nameErr, setNameErr] = useState(false);
  const [type, setType] = useState("SCPU");
  const [status, setStatus] = useState("ACTIVE");
  const [details, setDetails] = useState(emptyDetails("SCPU"));
  const [owners, setOwners] = useState([]); // [{id, user_id, perc}]

  const { members } = useGetMembersByCommunityIdQuery(
    { communityId: community.id, excludeDeleted: "true" },
    {
      selectFromResult: ({ data }) => ({ members: (data && selectAllMembers(data)) || [] }),
    }
  );

  const [addNewResource, { isLoading: isCreating }] = useAddNewResourceMutation();

  const typeLabel = (tp) => t(`new_resource.selects.types.${tp}`, { defaultValue: tp });
  const visual = resourceVisual(type);
  const fields = DETAIL_FIELDS[type] || [];

  const steps = [
    { label: t("new_resource.steps.basic_info"), sub: t("new_resource.steps.basic_sub") },
    { label: t("new_resource.steps.details"), sub: t("new_resource.steps.details_sub") },
  ];

  // ── Owners ────────────────────────────────────────────────────────────────
  const ownerTotal = owners.reduce((s, o) => s + (+o.perc || 0), 0);
  const freeMembers = (currentId) =>
    members.filter((m) => m.id === currentId || !owners.some((o) => o.user_id === m.id));

  const addOwner = () => {
    const m = members.find((mm) => !owners.some((o) => o.user_id === mm.id));
    if (!m) return;
    setOwners((os) => [
      ...os,
      { id: `o${os.length}-${m.id}`, user_id: m.id, perc: Math.max(0, 100 - ownerTotal) },
    ]);
  };
  const setOwner = (id, patch) =>
    setOwners((os) => os.map((o) => (o.id === id ? { ...o, ...patch } : o)));
  const rmOwner = (id) => setOwners((os) => os.filter((o) => o.id !== id));

  // ── Navigation ────────────────────────────────────────────────────────────
  const backToResources = () => navigate(`/communities/${community.id}/infrastructure/resources`);
  const next = () => {
    if (!name.trim()) {
      setNameErr(true);
      return;
    }
    setStep(1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ── Create ────────────────────────────────────────────────────────────────
  const buildTypePayload = () => {
    const payload = {};
    fields.forEach((f) => {
      payload[f.key] = f.input ? details[f.key] : Number(details[f.key]) || 0;
    });
    return { [`${type.toLowerCase()}_payload`]: payload };
  };

  const onCreate = async () => {
    if (isCreating) return;
    const ownerships = owners
      .filter((o) => o.user_id && +o.perc > 0)
      .map((o) => ({ user_id: o.user_id, perc_ownership: +o.perc }));
    try {
      await addNewResource({
        resource_payload: {
          name: name.trim(),
          type,
          community_id: community.id,
          status,
        },
        ...(ownerships.length > 0 ? { ownerships_payload: ownerships } : {}),
        ...buildTypePayload(),
      }).unwrap();
      dispatch(
        openSuccessAlert({
          title: t("new_resource.header.title"),
          description: t("new_resource.notifications.created", { name: name.trim() }),
        })
      );
      backToResources();
    } catch (err) {
      dispatch(openNotificationPopup({ notification: err, type: "error" }));
    }
  };

  return (
    <div>
      <HelpFloatingButtonV2 helpScreen="resource-edit" />
      <Crumbs
        items={[
          { label: community?.name || "—" },
          { label: t("infrastructure", { ns: "sidenav" }) },
          { label: t("title"), to: `/communities/${community.id}/infrastructure/resources` },
          { label: t("new_resource.header.title") },
        ]}
      />

      <div className="rec-page-head">
        <Button variant="ghost" size="sm" onClick={backToResources}>
          <Icon name="arrow_back" />
          {t("new_resource.back")}
        </Button>
      </div>

      <div className="rec-wiz-wrap">
        <WizSteps step={step} steps={steps} />

        <div className="rec-card rec-card-pad">
          {step === 0 ? (
            <>
              <div className="rec-wiz-card-head">
                <h2>{t("new_resource.steps.basic_info")}</h2>
                <p>{t("new_resource.steps.basic_hint")}</p>
              </div>

              <div className="flex flex-col gap-[18px]">
                <div className="rec-field">
                  <span className="text-[12.5px] font-semibold text-ink-2">
                    {t("new_resource.form.designation.label")}
                  </span>
                  <input
                    className={`rec-input ${nameErr ? "err" : ""}`}
                    value={name}
                    placeholder={t("new_resource.form.designation.placeholder")}
                    onChange={(e) => {
                      setName(e.target.value);
                      if (nameErr && e.target.value.trim()) setNameErr(false);
                    }}
                  />
                  {nameErr && (
                    <small className="rec-field-err">
                      <Icon name="error" style={{ fontSize: 14 }} />
                      {t("new_resource.form.designation.error_required")}
                    </small>
                  )}
                </div>

                <div className="rec-field">
                  <span className="text-[12.5px] font-semibold text-ink-2">
                    {t("new_resource.form.type.label")}
                  </span>
                  <div className="rec-type-pick">
                    {RESOURCE_TYPES.map((tp) => {
                      const v = resourceVisual(tp);
                      const on = type === tp;
                      return (
                        <button
                          key={tp}
                          type="button"
                          className={`rec-type-opt ${on ? "on" : ""}`}
                          onClick={() => {
                            setType(tp);
                            setDetails(emptyDetails(tp));
                          }}
                        >
                          <div
                            className={`rec-kpi-ic ${v.tint}`}
                            style={{ width: 36, height: 36, borderRadius: 10 }}
                          >
                            <Icon name={v.icon} fill style={{ fontSize: 19 }} />
                          </div>
                          <span>{typeLabel(tp)}</span>
                          {on && <Icon name="check_circle" fill className="rec-type-check" />}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="rec-field">
                  <span className="text-[12.5px] font-semibold text-ink-2">
                    {t("new_resource.form.status.label")}
                  </span>
                  <div>
                    <Segmented
                      value={status}
                      options={[
                        { value: "ACTIVE", label: t("new_resource.status_options.active") },
                        { value: "INACTIVE", label: t("new_resource.status_options.inactive") },
                      ]}
                      onChange={setStatus}
                    />
                  </div>
                </div>

                <div>
                  <div className="flex items-center gap-[10px] mb-1">
                    <span className="text-[12.5px] font-semibold text-ink-2">
                      {t("single.ownerships.title")}
                    </span>
                    <Chip tone="gray">{t("new_resource.owners_optional")}</Chip>
                    {owners.length > 0 && (
                      <span className="ml-auto">
                        <Chip tone={ownerTotal === 100 ? "green" : "gray"}>
                          {t("new_resource.owners_assigned", { total: ownerTotal })}
                        </Chip>
                      </span>
                    )}
                  </div>
                  <p className="m-0 mb-[10px] text-[12.5px] text-ink-3">
                    {t("new_resource.owners_hint")}
                  </p>

                  {owners.length > 0 && (
                    <div className="rec-owner-list">
                      {owners.map((o) => {
                        const memberName = members.find((m) => m.id === o.user_id)?.name || "";
                        return (
                          <div key={o.id} className="rec-owner-row">
                            <div className="rec-av-sm" style={{ background: avColor(memberName) }}>
                              {initials(memberName)}
                            </div>
                            <select
                              className="rec-input"
                              style={{ height: 36, fontSize: 13 }}
                              value={o.user_id}
                              onChange={(e) => setOwner(o.id, { user_id: e.target.value })}
                            >
                              {freeMembers(o.user_id).map((m) => (
                                <option key={m.id} value={m.id}>
                                  {m.name}
                                </option>
                              ))}
                            </select>
                            <div className="rec-input-wrap w-[110px] shrink-0">
                              <input
                                className="rec-input"
                                type="number"
                                min="0"
                                max="100"
                                style={{ height: 36, fontSize: 13, paddingRight: 30 }}
                                value={o.perc}
                                onChange={(e) => setOwner(o.id, { perc: e.target.value })}
                              />
                              <span className="absolute right-[11px] text-[12px] font-semibold text-ink-3">
                                %
                              </span>
                            </div>
                            <button
                              type="button"
                              className="rec-icon-btn !w-[34px] !h-[34px]"
                              title={t("new_resource.owners_remove")}
                              onClick={() => rmOwner(o.id)}
                            >
                              <Icon name="delete" style={{ fontSize: 18 }} />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={addOwner}
                    disabled={owners.length >= members.length}
                  >
                    <Icon name="person_add" />
                    {t("new_resource.owners_add")}
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="rec-wiz-card-head flex items-center gap-[14px]">
                <div
                  className={`rec-kpi-ic ${visual.tint}`}
                  style={{ width: 46, height: 46, borderRadius: 13, flexShrink: 0 }}
                >
                  <Icon name={visual.icon} fill style={{ fontSize: 23 }} />
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="flex items-center gap-[10px]">
                    {name}
                    {status === "ACTIVE" ? (
                      <Chip tone="green">{t("new_resource.status_options.active")}</Chip>
                    ) : (
                      <Chip tone="red">{t("new_resource.status_options.inactive")}</Chip>
                    )}
                  </h2>
                  <p>{`${typeLabel(type)} · ${t("new_resource.steps.details")}`}</p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-[14px]">
                {fields.map((f) =>
                  f.input === "select" ? (
                    <FieldSelect
                      key={f.key}
                      label={t(`new_resource.form.${f.labelKey}.label`)}
                      value={details[f.key]}
                      options={f.options}
                      onChange={(v) => setDetails((s) => ({ ...s, [f.key]: v }))}
                    />
                  ) : (
                    <FieldText
                      key={f.key}
                      label={t(`new_resource.form.${f.labelKey}.label`)}
                      type={f.input === "text" ? "text" : "number"}
                      value={details[f.key]}
                      onChange={(v) => setDetails((s) => ({ ...s, [f.key]: v }))}
                    />
                  )
                )}
              </div>
            </>
          )}

          <div className="rec-wiz-foot">
            <Button variant="ghost" onClick={backToResources}>
              {t("new.cancel", { ns: "community" })}
            </Button>
            <div className="flex gap-[10px]">
              {step === 1 && (
                <Button variant="ghost" onClick={() => setStep(0)}>
                  <Icon name="arrow_back" />
                  {t("new_resource.previous")}
                </Button>
              )}
              {step === 0 ? (
                <Button onClick={next}>
                  {t("new_resource.next")}
                  <Icon name="arrow_forward" />
                </Button>
              ) : (
                <Button disabled={isCreating} onClick={onCreate}>
                  <Icon name="check" />
                  {t("new_resource.submit")}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default NewResource;
